import { ProjectsService } from '@beeboard/common';
import { defineStore } from 'pinia';
import { useMilestoneProjectRulesStore } from './milestoneProjectRulesStore';
import { usePhaseProjectStore } from './phaseProjectStore';
import { useProjectStore } from './projectStore';
import type {
  MilestoneCreate,
  MilestoneUpdate,
  ApprovalCreate,
  Milestone,
  Comment,
  CommentCreateRequest,
  CommentUpdateRequest,
} from '@beeboard/common';

export type MilestoneTemplateStoreState = {
  projects: Record<string, Milestone[]>;
};

export const useMilestoneProjectStore = defineStore('milestoneProjectStore', {
  state: (): MilestoneTemplateStoreState => ({
    projects: {} as Record<string, Milestone[]>,
  }),

  getters: {
    milestone(state) {
      return (projectId: string, milestoneId: string): Milestone => {
        const project = state.projects[projectId];
        if (!project) {
          throw new Error('Missing milestone in milestoneStore');
        }
        const milestone = project.find((mil) => mil.id === milestoneId);
        if (!milestone) {
          throw new Error('Missing milestone in milestoneStore');
        }

        return milestone;
      };
    },

    hasMilestone(state) {
      return (projectId: string, milestoneId: string) => {
        const project = state.projects[projectId];

        return !!project?.find((mil) => mil.id === milestoneId);
      };
    },

    isIntermediateMilestone() {
      return (projectId: string, milestoneId: string): boolean => {
        const projectStore = useProjectStore();
        const project = projectStore.project(projectId);

        return project.phases.some((phase) => {
          return phase.intermediate_milestones.some(
            (milestone) => milestone.id === milestoneId,
          );
        });
      };
    },

    availableFunctions() {
      return (projectId: string, milestoneId: string) => {
        const projectStore = useProjectStore();
        const milestone = this.milestone(projectId, milestoneId);
        const approversIds =
          milestone.approvals.map(({ approver }) => approver.function_id) || [];

        return projectStore
          .functions(projectId)
          .filter(({ id }) => !approversIds.includes(id));
      };
    },
  },

  actions: {
    write(projectId: string, milestone: Milestone): Milestone {
      const project = this.getProject(projectId);

      const oldMilestone = project.find((mil) => mil.id === milestone.id);
      if (oldMilestone) {
        Object.assign(oldMilestone, milestone);
      } else {
        project.push(milestone);
      }

      return project.find((mil) => mil.id === milestone.id) as Milestone;
    },

    getProject(projectId: string) {
      if (!this.projects[projectId]) {
        this.projects[projectId] = [];
      }

      return this.projects[projectId];
    },

    async createMilestone(
      projectId: string,
      phaseId: string,
      payload: MilestoneCreate,
    ) {
      const milestone = await ProjectsService.addMilestoneToProject(
        projectId,
        payload,
      );
      this.write(projectId, milestone);
      const phase = usePhaseProjectStore().phase(projectId, phaseId);
      const summary = Object.assign(
        {
          required_activities: [] as string[],
          activities: [],
        },
        milestone,
        { deadline: milestone.current_deadline as string },
      );
      phase.intermediate_milestones.push(summary);

      return this.milestone(projectId, milestone.id);
    },

    async updateDeadline(
      projectId: string,
      milestoneId: string,
      payload: MilestoneUpdate,
    ) {
      const milestone = await ProjectsService.updateProjectMilestone(
        projectId,
        milestoneId,
        payload,
      );

      const persistedMilestone = this.write(projectId, milestone);
      if (!persistedMilestone) {
        return;
      }

      const projectStore = useProjectStore();
      if (!projectStore.hasProject(projectId)) {
        return;
      }
      if (payload.force_update_deadline || payload.phase) {
        await projectStore.loadProject(projectId);
      } else {
        const summary = projectStore.milestoneSummary(projectId, milestoneId);
        summary.current_deadline = persistedMilestone.current_deadline;
      }
    },

    async loadMilestone(projectId: string, milestoneId: string) {
      return ProjectsService.getMilestoneById(projectId, milestoneId).then(
        (milestone) => this.write(projectId, milestone),
      );
    },

    async addApprover(
      projectId: string,
      milestoneId: string,
      body: ApprovalCreate,
    ) {
      const approval = await ProjectsService.addMilestoneApprovalToProject(
        projectId,
        milestoneId,
        body,
      );
      const milestone = this.milestone(projectId, milestoneId);
      milestone.approvals.push(approval);
    },

    async removeApprover(
      projectId: string,
      milestoneId: string,
      approvalId: string,
    ) {
      await ProjectsService.removeProjectMilestoneApproval(
        projectId,
        milestoneId,
        approvalId,
      );
      const milestone = this.milestone(projectId, milestoneId);
      const index = milestone.approvals.findIndex(
        ({ id }) => id === approvalId,
      );
      milestone.approvals?.splice(index, 1);
    },

    async updateApprover(
      projectId: string,
      milestoneId: string,
      approvalId: string,
      function_id?: string,
      user_id?: string | null,
      state?: 'pending' | 'approved' | 'rejected',
    ) {
      const newApproval = await ProjectsService.updateMilestoneApprover(
        projectId,
        milestoneId,
        approvalId,
        {
          function_id,
          user_id,
          state,
        },
      );
      const activity = this.milestone(projectId, milestoneId);
      const oldApproval = activity.approvals.find(
        ({ id }) => id === approvalId,
      );
      if (!oldApproval) {
        throw new Error(`No approver found`);
      }
      Object.assign(oldApproval, newApproval);
    },

    async updateApproverState(
      projectId: string,
      milestoneId: string,
      approvalId: string,
      state?: 'pending' | 'approved' | 'rejected',
    ) {
      const newApproval = await ProjectsService.updateMilestoneApprover(
        projectId,
        milestoneId,
        approvalId,
        {
          state,
        },
      );
      const activity = this.milestone(projectId, milestoneId);
      const oldApproval = activity.approvals.find(
        ({ id }) => id === approvalId,
      );
      if (!oldApproval) {
        throw new Error(`No approver found`);
      }
      Object.assign(oldApproval, newApproval);
    },

    async update(
      projectId: string,
      milestoneId: string,
      payload: MilestoneUpdate,
    ) {
      const milestone = await ProjectsService.updateProjectMilestone(
        projectId,
        milestoneId,
        payload,
      );
      this.write(projectId, milestone);
      await useProjectStore().loadProject(projectId);
    },

    canBeRemoved(projectId: string, milestoneId: string) {
      const phase = usePhaseProjectStore().phaseForMilestone(
        projectId,
        milestoneId,
      );

      const isFinalMilestone = phase?.final_milestone.id === milestoneId;
      if (isFinalMilestone) {
        return false;
      }

      return true;
    },

    hasActivities(projectId: string, milestoneId: string) {
      const milestone = this.milestone(projectId, milestoneId);

      return milestone.activities && milestone.activities.length > 0;
    },

    async remove(projectId: string, milestoneId: string) {
      const errors =
        useMilestoneProjectRulesStore().validateRemoveMilestoneRequest(
          projectId,
          milestoneId,
        );
      if (errors) {
        throw errors;
      }

      return ProjectsService.removeProjectMilestone(
        projectId,
        milestoneId,
      ).then(() => useProjectStore().loadProject(projectId));
    },

    async getApprovals(projectId: string, milestoneId: string) {
      return await this.loadMilestone(projectId, milestoneId).then(
        (milestone) => milestone.approvals,
      );
    },

    async requestApproval(
      projectId: string,
      milestoneId: string,
      approvalId: string,
    ) {
      await ProjectsService.requestMilestoneApproval(
        projectId,
        milestoneId,
        approvalId,
      );
    },

    async addMilestoneComment(
      projectId: string,
      milestoneId: string,
      payload: CommentCreateRequest,
    ): Promise<Comment> {
      const comment = await ProjectsService.addMilestoneComment(
        projectId,
        milestoneId,
        payload,
      );
      const milestone = this.milestone(projectId, milestoneId);
      milestone.comments.unshift(comment);

      return comment;
    },

    async updateMilestoneComment(
      projectId: string,
      milestoneId: string,
      commentId: string,
      payload: CommentUpdateRequest,
    ): Promise<Comment> {
      const updatedComment = await ProjectsService.updateMilestoneComment(
        projectId,
        milestoneId,
        commentId,
        payload,
      );
      const milestone = this.milestone(projectId, milestoneId);
      const comment = milestone.comments.find(({ id }) => id === commentId);
      if (comment) {
        Object.assign(comment, updatedComment);
      }

      return updatedComment;
    },

    async removeMilestoneComment(
      projectId: string,
      milestoneId: string,
      commentId: string,
    ): Promise<void> {
      await ProjectsService.removeMilestoneComment(
        projectId,
        milestoneId,
        commentId,
      );
      const milestone = this.milestone(projectId, milestoneId);
      const commentIndex = milestone.comments.findIndex(
        ({ id }) => id === commentId,
      );
      if (commentIndex > -1) {
        milestone.comments.splice(commentIndex, 1);
      }
    },
  },
});
